/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import shortid from 'shortid';
import ExpandableText from 'components/ExpandableText/ExpandableText';

import styles from './TopicTable.module.scss';

/**
 * This is the TopicGroup component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function TopicGroup({
  title,
  view,
  topics,
  filterYears,
  collapsed,
  onToggleGroup,
  labels,
  summarize,
}) {
  const [id] = useState(shortid.generate());

  // Sum points of all topics
  const sum = topics.reduce(
    (curr, t) => curr + Object.values(t.points).reduce((c, p) => c + p, 0),
    0
  );

  // Calculate the number of year columns to show, based on the year filter
  const yearColumns = filterYears[1] - filterYears[0] + 1;

  // Generate array of the relevant years
  const yearArray = Array(filterYears[1] - filterYears[0] + 1)
    .fill()
    .map((_, idx) => filterYears[0] + idx);

  // Filter out only the topics that have defined points in any year within the defined range
  const visibleTopics = topics.reduce((curr, t) => {
    // Check if at least one topic has any points in the defined year range
    const hasPoints = Object.keys(t.points)
      .map((key) => parseInt(key, 10))
      .filter((key) => yearArray.includes(key))
      .some((year) => t.points[year] !== null);

    return hasPoints ? [...curr, t] : curr;
  }, []);

  function renderDesktopTable() {
    return (
      <table className={styles.table}>
        <colgroup>
          <col />
          <col />
          <col span={yearColumns + 1} />
        </colgroup>
        <thead>
          <tr>
            <th className={styles.code}>{labels.code}</th>
            <th className={styles.name}>{labels.topic}</th>
            <th colSpan={yearColumns} scope="colgroup">
              {labels.pointsPerYear}
            </th>
            <td></td>
          </tr>
          <tr>
            <td className={styles.code}></td>
            <td className={styles.name}></td>
            {yearArray.map((y) => (
              <th key={y} className={styles.points}>
                {y}. {labels.year}
              </th>
            ))}
            <th className={styles.total}>{labels.total}</th>
          </tr>
        </thead>
        <tbody>
          {visibleTopics.map((t) => (
            <tr key={t.code}>
              <th className={styles.code}>
                <a href={t.url} className="link">
                  {t.code}
                </a>
              </th>
              <td className={styles.name}>{t.title}</td>
              {yearArray.map((y) => (
                <td className={styles.points} key={y}>
                  {t.points[y] ?? ''}
                </td>
              ))}
              <td className={styles.total}>
                {Object.values(t.points).reduce((c, p) => c + p, 0)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function renderMobileTable() {
    return (
      <table className={styles.table}>
        <colgroup>
          <col />
          <col span={yearColumns} />
        </colgroup>
        <thead>
          <tr>
            <th className={styles.name}>{labels.topic}</th>
            <th colSpan={yearColumns} scope="colgroup">
              {labels.pointsPerYearShort}
            </th>
          </tr>
          <tr>
            <th className={styles.name}></th>
            {yearArray.map((y) => (
              <th key={y} className={styles.points}>
                {y}. {labels.year}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleTopics.map((t) => (
            <tr key={t.code}>
              <td className={styles.name}>
                <a href={t.url} className="link">
                  {t.code}
                </a>
                <p>{t.title}</p>
              </td>
              {yearArray.map((y) => (
                <td className={styles.points} key={y}>
                  {t.points[y] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  const finalTitle = summarize ? `${title} (${sum} ${labels.credits})` : title;

  return (
    <div className={styles.main}>
      <button
        type="button"
        onClick={() => onToggleGroup(title)}
        className={styles.title}
        aria-expanded={!collapsed ? 'true' : 'false'}
        aria-controls={id}
      >
        <h3 id={`${id}_label`}>{finalTitle}</h3>
        <div
          className={classNames(styles.icon, { [styles.collapsed]: collapsed })}
          aria-hidden
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.3212 11.7024L8.87243 7.25366L13.3212 2.80491C13.45 2.67613 13.45 2.4654 13.3212 2.33662L11.9163 0.93175C11.7875 0.80297 11.5768 0.80297 11.448 0.93175L6.99927 5.3805L2.55052 0.93175C2.42174 0.80297 2.21101 0.80297 2.08223 0.93175L0.677359 2.33662C0.548579 2.4654 0.548579 2.67613 0.677359 2.80491L5.12611 7.25366L0.677359 11.7024C0.54858 11.8312 0.54858 12.0419 0.677359 12.1707L2.08223 13.5756C2.21101 13.7043 2.42174 13.7043 2.55052 13.5756L6.99927 9.12682L11.448 13.5756C11.5768 13.7043 11.7875 13.7043 11.9163 13.5756L13.3212 12.1707C13.45 12.0419 13.45 11.8312 13.3212 11.7024Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </button>
      <div id={`${id}`} role="region" aria-labelledby={`${id}_label`}>
        <ExpandableText expanded={!collapsed}>
          <>
            {visibleTopics.length === 0 && (
              <p className={styles.empty}>{labels.empty}</p>
            )}
            {visibleTopics.length > 0 &&
              view === 'desktop' &&
              renderDesktopTable()}
            {visibleTopics.length > 0 &&
              view === 'mobile' &&
              renderMobileTable()}
          </>
        </ExpandableText>
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
TopicGroup.propTypes = {
  title: PropTypes.string,
  filterYears: PropTypes.array,
  view: PropTypes.oneOf(['desktop', 'mobile']),
  collapsed: PropTypes.bool,
  onToggleGroup: PropTypes.func,
  summarize: PropTypes.bool,
  topics: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      code: PropTypes.string,
      url: PropTypes.string,
      points: PropTypes.shape({
        1: PropTypes.number,
        2: PropTypes.number,
        3: PropTypes.number,
        4: PropTypes.number,
      }),
    })
  ),
  labels: PropTypes.shape({
    btnExpand: PropTypes.string,
    btnPullTogether: PropTypes.string,
    code: PropTypes.string,
    topic: PropTypes.string,
    pointsPerYear: PropTypes.string,
    pointsPerYearShort: PropTypes.string,
    total: PropTypes.string,
    year: PropTypes.string,
    empty: PropTypes.string,
    sum: PropTypes.string,
    credits: PropTypes.string,
  }),
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
TopicGroup.defaultProps = {
  title: '',
  view: 'desktop',
  topics: [],
  filterYears: [1, 4],
  collapsed: false,
  summarize: true,
  onToggleGroup: () => {},
};

export default TopicGroup;
